/* eslint-disable no-param-reassign */
import { Marker } from '@tomtom-international/web-sdk-maps/dist/maps.min';

import {
    boundingRect,
    trackSessionNamesForRect,
    setMarkerElementLoadingContent,
    setMarkerElementSessionNamesContent,
} from '../helpers';

/**
 * Radius (in meters) of area searched for track lines when user
 * clicks on the map.
 */
const RADIUS = 50;

const state = () => ({
    mapMarker: null,
});

const mutationNames = {
    SET_MAP_MARKER: 'setMapMarker',
};

const mutations = {
    /**
     * Sets current track names displayed beside map marker
     * @param {Object} newMapMarker - Instance of TT Marker.
     */
    [mutationNames.SET_MAP_MARKER]: (currentState, newMapMarker) => {
        currentState.mapMarker = newMapMarker;
    },
};

const actions = {
    /**
     * Converts given coordinates to a bounded rectangle which is
     * centered around given point. It should dispatch
     * getClosestTracks action upon completion.
     * @param {Object} context - Vuex context.
     * @param {Object} payload - Vuex payload object.
     * @param {number} payload.lng - Longitude of center point.
     * @param {number} payload.lat - Latitude of center point.
     * @param {Date} payload.startDate - Selected starting boundary date.
     * @param {Date} payload.endDate - Selected ending boundary date.
     * @param {Object} payload.map - Displayed TT Map instance.
     */
    selectCoordinates: (
        { commit, dispatch, state: { mapMarker } },
        {
            lng, lat, map, startDate, endDate,
        },
    ) => {
        if (mapMarker) {
            mapMarker.setLngLat([lng, lat]);

            setMarkerElementLoadingContent(mapMarker.getElement());
        } else {
            const markerElement = document.createElement('div');
            markerElement.classList.add('track-map-marker');

            setMarkerElementLoadingContent(markerElement);

            commit(
                mutationNames.SET_MAP_MARKER,
                new Marker({
                    element: markerElement,
                })
                    .setLngLat([lng, lat])
                    .addTo(map),
            );
        }

        dispatch(
            'getClosestSessionNames',
            {
                boundingBox: boundingRect({ lng, lat }, RADIUS),
                startDate,
                endDate,
            },
        );
    },

    /**
     * Contacts the MoMa API to obtain closest tracks' session names to the
     * center of given rectangle.
     * @param {Object} context - Vuex context.
     * @param {Object} payload - Vuex payload object.
     * @param {Array} payload.rectangle - Array containing coordinates of
     *     bounding box rectangle.
     * @param {number} payload.rectangle[0] - Longitude of south-west corner of
     *     bounding box.
     * @param {number} payload.rectangle[1] - Latitude of south-west corner of
     *     bounding box.
     * @param {number} payload.rectangle[2] - Longitude of north-east corner of
     *     bounding box.
     * @param {number} payload.rectangle[3] - Latitude of north-east corner of
     *     bounding box.
     * @param {Date} payload.startDate - Selected starting boundary date.
     * @param {Date} payload.endDate - Selected ending boundary date.
     */
    getClosestSessionNames: async (
        {
            dispatch, rootGetters: { momaURL }, state: { mapMarker },
        },
        { boundingBox, startDate, endDate },
    ) => {
        const sessionNames = await trackSessionNamesForRect({
            boundingBox,
            urlBuilder: momaURL,
            startDate,
            endDate,
        });

        let disabledTimeout;

        const onCloseClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            dispatch('clearMarker');

            if (disabledTimeout) {
                clearTimeout(disabledTimeout);
            }
        };

        const onCopyClick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (event.target.hasAttribute('disabled')) {
                return;
            }

            const joinedSessionNames = sessionNames.join('\n');

            if (window.navigator.clipboard && window.navigator.clipboard.writeText) {
                window.navigator.clipboard.writeText(joinedSessionNames);
            }

            if (window.clipboardData && window.clipboardData.setData) {
                // legacy IE clipboard support
                window.clipboardData.setData('Text', joinedSessionNames);
            }

            event.target.setAttribute('disabled', 'disabled');

            const previousText = event.target.innerHTML;

            event.target.innerHTML = 'COPIED';

            disabledTimeout = setTimeout(() => {
                if (event.target) {
                    event.target.removeAttribute('disabled');

                    event.target.innerHTML = previousText;
                }
            }, 3000);
        };

        setMarkerElementSessionNamesContent({
            element: mapMarker.getElement(),
            sessionNames,
            onCloseClick,
            onCopyClick,
            disabledTimeout,
        });
    },

    clearMarker({ state: { mapMarker } }) {
        if (mapMarker) {
            mapMarker.getElement().innerHTML = '';
        }
    },
};

export default {
    namespaced: true,

    state,
    mutations,
    actions,
};
